import { useMemo, useState } from "react";
import { ArrowLeft, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ReminderSheet } from "../components/ReminderSheet";
import { formatDateHeading, formatTime, isTodayOrFuture } from "../lib/date";
import type { AppData, AppointmentStatus, Id } from "../types";

interface PendingPageProps {
  data: AppData;
  setAppointmentStatus(id: Id, status: AppointmentStatus): void;
}

export function PendingPage({ data, setAppointmentStatus }: PendingPageProps) {
  const navigate = useNavigate();
  const [reminderId, setReminderId] = useState<Id | null>(null);
  const pending = useMemo(() => data.appointments
    .filter((item) => item.status === "pending" && isTodayOrFuture(item.startsAt))
    .map((appointment) => {
      const pet = data.pets.find((item) => item.id === appointment.petId);
      const owner = pet ? data.owners.find((item) => item.id === pet.ownerId) : undefined;
      return { appointment, pet, owner };
    })
    .filter((item): item is { appointment: typeof item.appointment; pet: NonNullable<typeof item.pet>; owner: NonNullable<typeof item.owner> } => Boolean(item.pet && item.owner))
    .sort((a, b) => a.appointment.startsAt.localeCompare(b.appointment.startsAt)), [data]);
  const reminderAppointment = data.appointments.find((item) => item.id === reminderId) || null;
  const reminderPet = reminderAppointment ? data.pets.find((item) => item.id === reminderAppointment.petId) || null : null;
  const reminderOwner = reminderPet ? data.owners.find((item) => item.id === reminderPet.ownerId) || null : null;

  return (
    <main className="app-shell pending-page">
      <header className="form-page__header">
        <button type="button" className="icon-button" aria-label="Wróć do agendy" onClick={() => navigate(-1)}><ArrowLeft size={22} /></button>
        <h1>Niepotwierdzone wizyty</h1>
      </header>

      {pending.length ? <div className="pending-list">
        {pending.map(({ appointment, pet, owner }, index) => {
          const previous = pending[index - 1];
          const newDay = !previous || new Date(previous.appointment.startsAt).toDateString() !== new Date(appointment.startsAt).toDateString();
          return <div key={appointment.id}>
            {newDay && <h2 className="date-heading">{formatDateHeading(appointment.startsAt)}</h2>}
            <article className="pending-row">
              <div className="pet-avatar" aria-hidden="true">{pet.avatar || "🐾"}</div>
              <div><h2>{pet.name}</h2><p>{owner.fullName}</p><strong>{formatTime(appointment.startsAt)}</strong></div>
              <div className="pending-row__actions">
                <button type="button" className="icon-button" aria-label={`Przypomnij ${owner.fullName}`} onClick={() => setReminderId(appointment.id)}><Bell size={20} /></button>
                <button type="button" className="confirm-button" onClick={() => setAppointmentStatus(appointment.id, "confirmed")}>Potwierdź</button>
              </div>
            </article>
          </div>;
        })}
      </div> : <div className="empty-state"><span>✓</span><h2>Brak niepotwierdzonych wizyt</h2><p>Nie masz wizyt wymagających kontaktu.</p></div>}

      <ReminderSheet open={Boolean(reminderId)} appointment={reminderAppointment} pet={reminderPet} owner={reminderOwner} onClose={() => setReminderId(null)} />
    </main>
  );
}
